/**
 * Durable audit log.
 *
 * Storage layout:
 *   - Source of truth: the `audit_log` table in Supabase (see
 *     `supabase/migrations/20260424140000_create_audit_log.sql`). It is
 *     append-only, RLS-protected, and never trimmed by the app.
 *   - Local mirror: every entry is also written to `localStorage` under
 *     `STORAGE_KEY`. The mirror gives us: offline writes, fast synchronous
 *     reads for callers that still run sync (e.g. the patient-storage vet
 *     backfill), and a short-term cache that survives network blips.
 *
 * Callers keep a simple sync surface:
 *   - `logAudit(event)`   writes synchronously to localStorage, returns the
 *                         entry, and pushes to Supabase in the background.
 *   - `getAuditLog(...)`  still reads the localStorage mirror synchronously.
 *
 * The admin page uses the async surface so it can see the full history:
 *   - `fetchRemoteAuditLog(filter)`
 *   - `fetchRemoteDistinctActors/Actions/EntityTypes()`
 *   - `syncRemoteAuditLogToCache()`
 *
 * Historical note: this module used to be pure localStorage and warned that
 * "a real CVM submission would require a tamper-evident server-side log".
 * The Supabase backing is that server-side log; the local mirror is now just
 * a UX cache. The audit rows it produces are the source for the
 * `<auditTrail>` block in `qentoros-study-data-with-audit.xml` (see
 * `src/utils/exportStudyDataXml.js`).
 */

import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";
import { getCurrentUserEmail } from "./auth";

const STORAGE_KEY = "auditLog";
const EVENT_NAME = "auditLogUpdated";
const REMOTE_UPDATE_EVENT = "auditLogRemoteUpdated";
const TABLE = "audit_log";

// Upper bound on the localStorage mirror so we do not bust the ~5 MB quota on
// long-lived installs. Supabase still has every entry; the mirror only needs
// to be big enough for the dashboard feed + patient-vet backfill heuristics.
const LOCAL_MIRROR_MAX = 5000;

// Pull size when the admin page asks Supabase for "all events matching these
// filters". Supabase's default row limit is 1000; we override so large-range
// exports still get the full history. Paging can be added later if a single
// export ever exceeds this.
const REMOTE_FETCH_LIMIT = 10000;

const safeRead = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const safeWrite = (entries) => {
  // Keep the most recent LOCAL_MIRROR_MAX entries in the cache so the tab
  // does not accumulate unbounded JSON. Newer entries are pushed first by
  // `logAudit`, so we trim from the tail.
  const trimmed =
    entries.length > LOCAL_MIRROR_MAX
      ? entries.slice(0, LOCAL_MIRROR_MAX)
      : entries;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed));
    window.dispatchEvent(
      new CustomEvent(EVENT_NAME, { detail: { count: trimmed.length } })
    );
  } catch (err) {
    // Quota hit. Drop the oldest 20% and retry once; if that also fails,
    // give up on the cache for this write. Supabase still has the entry.
    const keep = Math.max(1, Math.floor(trimmed.length * 0.8));
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(trimmed.slice(0, keep)));
    } catch {
      // eslint-disable-next-line no-console
      console.warn("Audit log cache write failed", err);
    }
  }
};

const makeId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const normalizeActorEmail = (value) =>
  typeof value === "string" ? value.trim().toLowerCase() : "";

// Translate a row from Supabase into the shape callers already expect. The
// app uses camelCase throughout; Postgres uses snake_case. The `id` we return
// is the server uuid (falling back to the client id for unsynced entries).
const fromRemoteRow = (row) => ({
  id: row.id,
  clientId: row.client_id || null,
  timestamp: row.timestamp,
  // Server insertion time. Independent of the client clock, so an auditor can
  // detect a skewed/backdated client `timestamp`. Null for local-cache-only
  // entries that never round-tripped through Supabase.
  createdAt: row.created_at || null,
  action: row.action,
  entityType: row.entity_type || "other",
  entityId: row.entity_id || null,
  entityLabel: row.entity_label || null,
  details: row.details || null,
  source: row.source || null,
  actorEmail: row.actor_email || "unknown",
});

const toRemoteRow = (entry) => ({
  client_id: entry.id,
  timestamp: entry.timestamp,
  actor_email: entry.actorEmail,
  action: entry.action,
  entity_type: entry.entityType || null,
  entity_id: entry.entityId || null,
  entity_label: entry.entityLabel || null,
  details: entry.details || null,
  source: entry.source || null,
});

// Fire-and-forget insert. We do not await this from `logAudit` because every
// existing call site is synchronous; instead we surface failures via console
// and a one-time flag so the cache continues to accept writes. A later
// reconciliation pass (see `syncRemoteAuditLogToCache`) can re-send entries
// that never made it to Supabase by matching on `client_id`.
const pushToRemote = (entry) => {
  if (!isSupabaseConfigured) return;
  supabase
    .from(TABLE)
    .insert(toRemoteRow(entry))
    .then(({ error }) => {
      if (error && error.code !== "23505" /* unique violation on retry */) {
        // eslint-disable-next-line no-console
        console.warn("Audit log remote insert failed", error.message || error);
      }
    })
    .catch((err) => {
      // eslint-disable-next-line no-console
      console.warn("Audit log remote insert threw", err);
    });
};

/**
 * Append a new entry to the audit log.
 *
 * @param {Object} event
 * @param {string} event.action       required, e.g. "patient.created"
 * @param {string} [event.entityType] e.g. "patient", "trial", "form", "session"
 * @param {string} [event.entityId]
 * @param {string} [event.entityLabel]
 * @param {Object} [event.details]    freeform JSON for the drawer / PDF
 * @param {string} [event.source]     component name that logged the event
 * @param {string} [event.actorEmail] overrides current user (useful on login)
 */
export const logAudit = (event = {}) => {
  if (!event.action) return null;

  const actorEmail =
    normalizeActorEmail(event.actorEmail) ||
    getCurrentUserEmail() ||
    "unknown";

  const entry = {
    id: makeId(),
    timestamp: new Date().toISOString(),
    action: event.action,
    entityType: event.entityType || "other",
    entityId: event.entityId || null,
    entityLabel: event.entityLabel || null,
    details: event.details || null,
    source: event.source || null,
    actorEmail,
  };

  const entries = safeRead();
  entries.unshift(entry);
  safeWrite(entries);

  pushToRemote(entry);

  return entry;
};

const applyLocalFilter = (entries, filter = {}) => {
  const { from, to, actor, action, entityType, entityId } = filter;
  const fromMs = from ? new Date(from).getTime() : null;
  const toMs = to ? new Date(to).getTime() : null;
  const actorNorm = actor ? normalizeActorEmail(actor) : null;

  return entries
    .filter((e) => {
      if (fromMs !== null && new Date(e.timestamp).getTime() < fromMs)
        return false;
      if (toMs !== null && new Date(e.timestamp).getTime() > toMs) return false;
      if (actorNorm && e.actorEmail !== actorNorm) return false;
      if (action && e.action !== action) return false;
      if (entityType && e.entityType !== entityType) return false;
      if (entityId && e.entityId !== entityId) return false;
      return true;
    })
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
};

/**
 * Synchronous local-cache read. Preserved for callers that cannot go async
 * (e.g. `patientStorage.backfillAssignedVetEmails` runs during render). The
 * admin audit page prefers `fetchRemoteAuditLog` for completeness.
 */
export const getAuditLog = (filter = {}) => {
  return applyLocalFilter(safeRead(), filter);
};

export const getDistinctActors = () => {
  const entries = safeRead();
  return Array.from(
    new Set(entries.map((e) => e.actorEmail).filter(Boolean))
  ).sort();
};

export const getDistinctActions = () => {
  const entries = safeRead();
  return Array.from(
    new Set(entries.map((e) => e.action).filter(Boolean))
  ).sort();
};

export const getDistinctEntityTypes = () => {
  const entries = safeRead();
  return Array.from(
    new Set(entries.map((e) => e.entityType).filter(Boolean))
  ).sort();
};

/**
 * Query the Supabase `audit_log` table directly, applying the same filters
 * the admin page exposes in its UI. Falls back to the local cache when
 * Supabase is unreachable or misconfigured so the page never hard-fails.
 */
export const fetchRemoteAuditLog = async (filter = {}) => {
  if (!isSupabaseConfigured) {
    return applyLocalFilter(safeRead(), filter);
  }

  let query = supabase
    .from(TABLE)
    .select("*")
    .order("timestamp", { ascending: false })
    .limit(REMOTE_FETCH_LIMIT);

  if (filter.from) query = query.gte("timestamp", new Date(filter.from).toISOString());
  if (filter.to) query = query.lte("timestamp", new Date(filter.to).toISOString());
  if (filter.actor) query = query.eq("actor_email", normalizeActorEmail(filter.actor));
  if (filter.action) query = query.eq("action", filter.action);
  if (filter.entityType) query = query.eq("entity_type", filter.entityType);
  if (filter.entityId) query = query.eq("entity_id", filter.entityId);

  const { data, error } = await query;
  if (error) {
    // eslint-disable-next-line no-console
    console.warn(
      "Audit log remote fetch failed, falling back to local cache:",
      error.message || error
    );
    return applyLocalFilter(safeRead(), filter);
  }
  return (data || []).map(fromRemoteRow);
};

// Page size for the exhaustive export fetch below. Supabase caps a single
// `.range()` window; 1000 is the server default and a safe batch size.
const REMOTE_PAGE_SIZE = 1000;

/**
 * Fetch the COMPLETE audit trail matching `filter`, paging through Supabase so
 * the result is never silently capped. `fetchRemoteAuditLog` stops at
 * REMOTE_FETCH_LIMIT, which is fine for the interactive admin table but would
 * quietly truncate an FDA/CVM export once the log exceeds that bound. The
 * export path uses this instead so the submitted audit trail is exhaustive.
 *
 * Falls back to the local cache (also complete, up to the mirror cap) when
 * Supabase is unreachable, matching `fetchRemoteAuditLog`'s behaviour.
 */
export const fetchAllRemoteAuditLog = async (filter = {}) => {
  if (!isSupabaseConfigured) {
    return applyLocalFilter(safeRead(), filter);
  }

  const all = [];
  for (let offset = 0; ; offset += REMOTE_PAGE_SIZE) {
    let query = supabase
      .from(TABLE)
      .select("*")
      .order("timestamp", { ascending: false })
      .range(offset, offset + REMOTE_PAGE_SIZE - 1);

    if (filter.from)
      query = query.gte("timestamp", new Date(filter.from).toISOString());
    if (filter.to)
      query = query.lte("timestamp", new Date(filter.to).toISOString());
    if (filter.actor)
      query = query.eq("actor_email", normalizeActorEmail(filter.actor));
    if (filter.action) query = query.eq("action", filter.action);
    if (filter.entityType) query = query.eq("entity_type", filter.entityType);

    // eslint-disable-next-line no-await-in-loop
    const { data, error } = await query;
    if (error) {
      // eslint-disable-next-line no-console
      console.warn(
        "Audit log full fetch failed, falling back to local cache:",
        error.message || error
      );
      return applyLocalFilter(safeRead(), filter);
    }

    const batch = data || [];
    all.push(...batch);
    if (batch.length < REMOTE_PAGE_SIZE) break;
  }

  return all.map(fromRemoteRow);
};

/**
 * Refresh the localStorage mirror from Supabase. The admin page calls this
 * on mount so subsequent synchronous reads (dashboard feed, patient
 * backfill) include entries created from other devices or other tabs.
 */
export const syncRemoteAuditLogToCache = async () => {
  if (!isSupabaseConfigured) return false;

  const { data, error } = await supabase
    .from(TABLE)
    .select("*")
    .order("timestamp", { ascending: false })
    .limit(LOCAL_MIRROR_MAX);

  if (error) {
    // eslint-disable-next-line no-console
    console.warn(
      "Audit log cache sync failed:",
      error.message || error
    );
    return false;
  }

  const remote = (data || []).map(fromRemoteRow);

  // Preserve any locally-created entries that have not yet landed in
  // Supabase (network was offline at insert time). We key by client_id /
  // id so a later retry by the server does not duplicate them.
  const remoteIds = new Set(
    remote.flatMap((e) => [e.id, e.clientId].filter(Boolean))
  );
  const localOnly = safeRead().filter((e) => !remoteIds.has(e.id));

  const merged = [...remote, ...localOnly].sort(
    (a, b) => new Date(b.timestamp) - new Date(a.timestamp)
  );
  safeWrite(merged);
  window.dispatchEvent(new CustomEvent(REMOTE_UPDATE_EVENT));
  return true;
};

const fetchRemoteDistinct = async (column) => {
  if (!isSupabaseConfigured) return [];
  const { data, error } = await supabase
    .from(TABLE)
    .select(column)
    .limit(REMOTE_FETCH_LIMIT);
  if (error) {
    // eslint-disable-next-line no-console
    console.warn(
      `Audit log remote distinct(${column}) failed:`,
      error.message || error
    );
    return [];
  }
  return Array.from(
    new Set((data || []).map((row) => row[column]).filter(Boolean))
  ).sort();
};

export const fetchRemoteDistinctActors = () =>
  fetchRemoteDistinct("actor_email");

export const fetchRemoteDistinctActions = () => fetchRemoteDistinct("action");

export const fetchRemoteDistinctEntityTypes = () =>
  fetchRemoteDistinct("entity_type");

// NOTE: There is intentionally no `clearAuditLog`. An FDA/CVM audit trail must
// be append-only and non-erasable (21 CFR Part 11 §11.10(e)); the ability to
// delete history is a compliance violation, not a feature. The server-side
// DELETE policy is likewise revoked in
// `supabase/migrations/20260711120000_lock_audit_log_append_only.sql`.
// Corrections are made by appending a new entry, never by mutating or
// removing existing rows.
